import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import { Button } from 'primeng/button';
import { bdDialogCloseBehavior, BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';
import { Tooltip } from 'primeng/tooltip';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  Stepper,
  StepList,
  StepPanel,
  StepPanels,
  Step,
} from 'primeng/stepper';

import {
  ItemSelectionData,
  MoveDialogResult,
  SelectedItem,
} from '../dialog.models';
import { BdUiProgressIndicatorComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { ShareableBatchRun } from '../batch-run';
import { BdMoveFormComponent } from './move-form/move-form.component';
import { BdMovePreviewComponent } from './move-preview/move-preview.component';

@Component({
  selector: 'bd-move-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (progressState() === 'idle') {
      <p-stepper [value]="activeStep()" [linear]="true">
        <p-step-list>
          <p-step [value]="1">Ziel</p-step>
          <p-step [value]="2">Ressourcen</p-step>
        </p-step-list>
        <p-step-panels>
          <p-step-panel [value]="1">
            <ng-template #content let-activateCallback="activateCallback">
              <bd-move-form
                [includeOwnResources]="includeOwnResources"
                (folderChange)="onFolderChange($event)"
              ></bd-move-form>
              <div class="flex flex-row gap-4 justify-end w-full pt-4">
                <p-button
                  label="Abbrechen"
                  severity="secondary"
                  [text]="true"
                  (onClick)="ref.close(null)"
                />
                <p-button
                  label="Weiter"
                  severity="primary"
                  bdDialogPrimaryAction
                  [disabled]="!selectedFolderId()"
                  [pTooltip]="
                    selectedFolderId() ? '' : 'Bitte einen Zielordner auswählen'
                  "
                  (onClick)="onStartPreview(activateCallback)"
                />
              </div>
            </ng-template>
          </p-step-panel>

          <p-step-panel [value]="2">
            <ng-template #content let-activateCallback="activateCallback">
              @if (selectedFolderId(); as targetId) {
                <bd-move-preview
                  [items]="items"
                  [targetFolderId]="targetId"
                ></bd-move-preview>
              }
              <div class="flex flex-row gap-4 justify-end w-full pt-4">
                <p-button
                  label="Zurück"
                  severity="secondary"
                  [text]="true"
                  (onClick)="onBackFromPreview(activateCallback)"
                />
                <p-button
                  label="Verschieben"
                  severity="primary"
                  bdDialogPrimaryAction
                  [disabled]="!analysisFinished()"
                  [pTooltip]="analysisFinished() ? '' : 'Prüfung läuft noch'"
                  (onClick)="onSubmitMove()"
                />
              </div>
            </ng-template>
          </p-step-panel>
        </p-step-panels>
      </p-stepper>
    } @else {
      <bd-ui-progress-indicator
        [state]="progressState()"
        actionLabel="Verschiebe Elemente…"
        doneLabel="Alle Elemente wurden erfolgreich verschoben."
        [completedCount]="completedCount()"
        [totalCount]="totalCount()"
        [errorCount]="errorCount()"
        (closed)="onClose()"
      ></bd-ui-progress-indicator>
    }
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    Button,
    Tooltip,
    Stepper,
    StepList,
    StepPanel,
    StepPanels,
    Step,
    BdMoveFormComponent,
    BdMovePreviewComponent,
    BdUiProgressIndicatorComponent,
  ],
})
export class BdMoveDialogComponent {
  public readonly ref = inject(DynamicDialogRef);
  public readonly config =
    inject<DynamicDialogConfig<ItemSelectionData>>(DynamicDialogConfig);
  private readonly _resourcesService = inject(BdShareablesService);

  public readonly activeStep = signal<number>(1);
  private readonly _batch = new ShareableBatchRun(inject(DestroyRef));
  public readonly progressState = this._batch.state;
  public readonly completedCount = this._batch.completed;
  public readonly errorCount = this._batch.failed;

  public readonly selectedFolderId = signal<string | null>(null);
  public readonly previewRef = viewChild(BdMovePreviewComponent);
  public readonly analysisFinished = computed(
    () => this.previewRef()?.analysisFinished() ?? false,
  );

  public readonly items: SelectedItem[] = this.config.data?.items ?? [];

  /** Defaults to true, so existing callers keep offering the own-resources root. */
  public readonly includeOwnResources: boolean =
    this.config.data?.includeOwnResources ?? true;

  public readonly totalCount = this._batch.total;

  public constructor() {
    // Escape / × wait for the running moves; afterwards they report the same
    // result as "Schließen".
    bdDialogCloseBehavior({
      busy: () => this.progressState() === 'running',
      resultOnClose: () =>
        this.progressState() === 'done' ? this._result() : undefined,
    });
  }

  public onFolderChange(folderId: string | null): void {
    this.selectedFolderId.set(folderId);
  }

  public onStartPreview(activateCallback: (step: number) => void): void {
    activateCallback(2);
    this.activeStep.set(2);
  }

  public onBackFromPreview(activateCallback: (step: number) => void): void {
    activateCallback(1);
    this.activeStep.set(1);
  }

  public onSubmitMove(): void {
    const target = this.selectedFolderId();
    if (!target) {
      return;
    }
    this._batch.run(this.items, (item) =>
      this._resourcesService.moveShareable(item.id, item.type, target),
    );
  }

  public onClose(): void {
    this.ref.close(this._result());
  }

  private _result(): MoveDialogResult {
    return { targetFolderId: this.selectedFolderId() ?? '' };
  }
}
